// The conditions table: a full-width, three-hourly, day-grouped grid of printed
// numbers under the map.
//
// This is the panel the client asked for, and it supersedes the conditions
// strip. The strip drew the same week as bars on the shared x-mapping, fitted
// to the track column's width; at ~4 px an hour there was no room for a number,
// so the numbers lived in the row labels and followed the playhead. That is a
// fine instrument and the wrong one: what was wanted is a TABLE you read across
// — every cell printing its own value, three-hourly so the digits fit, scrolling
// out to the horizon rather than compressing to fit.
//
// WHY CANVAS. A week of three-hourly columns over ten rows is ~600 cells, each
// with a number, some with a second line and an arrow — 1,800-odd DOM nodes
// rebuilt on every units flip, clock flip, theme change and vessel change. The
// charts in this console are hand-rolled canvas for the same reason, and canvas
// brings the other half for free: only the columns actually on screen are ever
// drawn, so horizontal scroll costs the same at hour 6 as at hour 160.
//
// The scroll is a transparent DOM overlay carrying a spacer of the full table
// width, sitting on top of a canvas that never moves. The browser owns the
// scrollbar, the wheel, the trackpad fling and the keyboard; we own one number,
// scrollLeft, which becomes the draw offset. Reimplementing momentum scrolling
// on a canvas is a well-known way to lose a week.
//
// Everything it shows comes from panel/rows.ts, which reads the HELD forecast —
// clicking a tab, flipping the clock or changing vessel never refetches.

import { getCursor, setCursor, timeAtIndex, subscribeCursor, timesOf } from "../time/cursor.js";
import { subscribeUnits } from "../ui/units.js";
import { displayParts, fmtDayLong, fmtHourCol, clockLabel, subscribeTimeMode } from "../ui/time.js";
import { getPref, setPref } from "../ui/prefs.js";
import { createGrip } from "../ui/grip.js";
import { WORK_GRADES } from "../ops/workability.js";
import { cssVar, drawArrowGlyph, glyphHeading, NIGHT_TINTS } from "../chart/marks.js";
import { infoButton } from "../help/popover.js";
import { columnAtTime, groupByDay, pickColumns, type AxisParts, type DayGroup } from "./columns.js";
import { dayPredictability, predictabilityBadge, predictabilityWord } from "./predictability.js";
import { ensembleWidthAt } from "../ops/briefing.js";
import {
  buildTabs,
  gradeIndexOf,
  MARKS,
  type Cell,
  type Row,
  type Tab,
  type TableInput,
} from "./rows.js";
import {
  contrastRatio,
  fitWeightScale,
  GRADE_WEIGHTS,
  NEUTRAL_WEIGHT,
  gradeFill,
  neutralFill,
  parseColor,
  readableInk,
  toCss,
  type RGB,
} from "./heat.js";

/** Column width. Wide enough for "12.3" beside an arrow at the table's type
 *  size, which is the whole reason the columns are three-hourly. */
const COL_W = 52;
const DAY_H = 21;
const HOUR_H = 15;
const HEAD_H = DAY_H + HOUR_H;

const PREF_OPEN = "ctable"; // "collapsed" | anything else = open
const PREF_TAB = "ctable.tab";

const FALLBACK: Record<string, string> = {
  "--surface": "#ffffff",
  "--surface-2": "#f4f6f8",
  "--line": "#d7dee5",
  "--line-2": "#c2ccd6",
  "--ink": "#16232b",
  "--ink-2": "#48575f",
  "--muted": "#78848f",
  "--accent": "#0f6b74",
};

/** Last resort when a custom property resolves to something unparseable — a
 *  mid grey reads as "no signal" in every theme, which is the truth. */
const GREY: RGB = [136, 136, 136];

function rgbVar(name: string): RGB {
  return parseColor(cssVar(name, FALLBACK[name] ?? "#888888")) ?? GREY;
}

export interface ConditionsTableInput extends Omit<TableInput, "columns"> {
  /** The point the table is describing, for the header. */
  place: string;
}

export interface ConditionsTable {
  /** Point the table at a forecast, or null to hide it. */
  update(input: ConditionsTableInput | null): void;
  /** Swap only what depends on the vessel/operation selection — the weather did
   *  not move, so the columns and the scroll position must not either. */
  refreshSelection(patch: Pick<TableInput, "vessel" | "limit" | "opLabel" | "workP">): void;
  /** The worst contrast ratio between a printed number and its own cell fill in
   *  the last draw. The dev harness asserts this across all three themes — a
   *  heatmap whose digits stop reading is a heatmap that has become decoration. */
  lastMinContrast(): number;
  destroy(): void;
}

export function createConditionsTable(parent: HTMLElement): ConditionsTable {
  // --- DOM ------------------------------------------------------------------
  const root = document.createElement("section");
  root.className = "ctable";
  root.hidden = true;

  const head = document.createElement("div");
  head.className = "ct-head";
  const title = document.createElement("span");
  title.className = "ct-title";
  title.textContent = "Conditions";
  const tabsEl = document.createElement("div");
  tabsEl.className = "ct-tabs";
  const scaleNote = document.createElement("span");
  scaleNote.className = "ct-scale";
  // The text lives in its own node so re-rendering it cannot wipe the ⓘ button
  // appended beside it.
  const scaleText = document.createElement("span");
  scaleNote.appendChild(scaleText);
  // The same grip the columns fold with (ui/grip.ts), laid on its side. This was
  // a 14px chevron glyph, which is the size nobody could find — and this table
  // is the surface most worth folding, since it is the one that costs the map
  // its height.
  //
  // It sits in the header rather than out on the seam above, because the map's
  // field legend is centred on that seam with 10px of clearance and a grip there
  // would land on top of it. The header bar survives the fold, so the way back
  // is never lost either way.
  const grip = createGrip({
    hideLabel: "Fold the conditions table",
    showLabel: "Unfold the conditions table",
    folded: getPref(PREF_OPEN) === "collapsed",
    onToggle: (folded) => setCollapsed(folded),
  });
  grip.root.classList.add("gutgrip-h");
  title.appendChild(infoButton("fc.table"));
  // The colour rule gets its own affordance beside the scale note, because
  // "what is this colour measured against" is the question the table invites.
  scaleNote.appendChild(infoButton("wk.limitColour"));
  head.append(title, tabsEl, scaleNote, grip.root);

  const body = document.createElement("div");
  body.className = "ct-body";
  const labels = document.createElement("div");
  labels.className = "ct-labels";
  const view = document.createElement("div");
  view.className = "ct-view";
  const canvas = document.createElement("canvas");
  const scroll = document.createElement("div");
  scroll.className = "ct-scroll";
  const spacer = document.createElement("div");
  spacer.className = "ct-spacer";
  scroll.appendChild(spacer);
  view.append(canvas, scroll);
  body.append(labels, view);

  const foot = document.createElement("div");
  foot.className = "ct-foot";
  const keyEl = document.createElement("div");
  keyEl.className = "ct-key";
  const srcEl = document.createElement("div");
  srcEl.className = "ct-src";
  foot.append(keyEl, srcEl);

  root.append(head, body, foot);
  parent.appendChild(root);

  const ctx = canvas.getContext("2d");
  if (ctx === null) throw new Error("conditions table: 2d context unavailable");
  const g = ctx;

  // --- State ----------------------------------------------------------------
  let input: ConditionsTableInput | null = null;
  let columns: number[] = [];
  let instants: number[] = [];
  let days: DayGroup[] = [];
  let badges: (string | null)[] = [];
  let badgeWords: (string | null)[] = [];
  let tabs: Tab[] = [];
  let activeId = typeof getPref(PREF_TAB) === "string" ? String(getPref(PREF_TAB)) : "conditions";
  let raf = false;
  let minContrast = Infinity;

  // The axis clock: ui/time.ts's timeline parts, which are the site's zone or
  // UTC exactly as the ruler and every chart tooltip read them. Passed in as a
  // function so panel/columns.ts stays clockless and testable.
  const partsOf = (t: number): AxisParts => displayParts(t);

  function activeTab(): Tab | null {
    return tabs.find((t) => t.id === activeId) ?? tabs[0] ?? null;
  }

  // --- Rebuild --------------------------------------------------------------

  function rebuildColumns(): void {
    if (input === null) {
      columns = [];
      instants = [];
      days = [];
      badges = [];
      badgeWords = [];
      return;
    }
    instants = timesOf(input.fc.times);
    columns = pickColumns(instants, partsOf);
    days = groupByDay(columns, instants, partsOf);
    // The predictability badge is per DAY and reads every hour of that day, not
    // just the eight that became columns — the spread the operator is being told
    // about is the day's, and throwing away two thirds of the hours to match the
    // column grid would make the number depend on the column spacing.
    const fc = input.fc;
    badges = days.map((d) => {
      const firstIdx = columns[d.first];
      const lastIdx = columns[d.last];
      const widths: (number | null)[] = [];
      for (let i = firstIdx; i <= lastIdx && i < fc.times.length; i++) {
        widths.push(ensembleWidthAt(fc, i));
      }
      return predictabilityBadge(dayPredictability(widths));
    });
    badgeWords = days.map((d) => {
      const firstIdx = columns[d.first];
      const lastIdx = columns[d.last];
      const widths: (number | null)[] = [];
      for (let i = firstIdx; i <= lastIdx && i < fc.times.length; i++) {
        widths.push(ensembleWidthAt(fc, i));
      }
      return predictabilityWord(dayPredictability(widths));
    });
  }

  function rebuildTabs(): void {
    tabs = input === null ? [] : buildTabs({ ...input, columns });
    if (!tabs.some((t) => t.id === activeId)) activeId = tabs[0]?.id ?? "conditions";
  }

  function renderTabs(): void {
    tabsEl.replaceChildren();
    for (const t of tabs) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "ct-tab";
      b.setAttribute("aria-pressed", String(t.id === activeId));
      if (t.id === activeId) b.classList.add("on");
      const name = document.createElement("span");
      name.textContent = t.label;
      b.appendChild(name);
      // The status is never the colour alone: the dot carries the grade and the
      // word beside it says the same thing in text, so the tab reads on a
      // greyscale printout and to an operator who cannot separate the hues.
      if (t.status !== null) {
        const dot = document.createElement("i");
        dot.className = `ct-dot ${WORK_GRADES[t.status.gradeIndex].cls}`;
        const word = document.createElement("em");
        word.textContent =
          t.status.noGo > 0 ? `${t.status.noGo} no-go` : t.status.label.toLowerCase();
        b.append(dot, word);
        b.title =
          t.status.noGo > 0
            ? `Worst in this tab: ${t.status.label}. ${t.status.noGo} of ${columns.length} columns hold a no-go cell.`
            : `Worst in this tab: ${t.status.label}.`;
      }
      b.addEventListener("click", () => {
        activeId = t.id;
        setPref(PREF_TAB, t.id);
        rebuildTabs();
        renderTabs();
        renderLabels();
        renderFoot();
        layout();
      });
      tabsEl.appendChild(b);
    }
  }

  function renderLabels(): void {
    labels.replaceChildren();
    const spacerTop = document.createElement("div");
    spacerTop.className = "ct-lspacer";
    spacerTop.style.height = `${HEAD_H}px`;
    labels.appendChild(spacerTop);
    const tab = activeTab();
    for (const row of tab?.rows ?? []) {
      const el = document.createElement("div");
      el.className = "ct-label";
      el.style.height = `${row.height}px`;
      const name = document.createElement("b");
      name.textContent = row.label;
      const unit = document.createElement("span");
      unit.textContent = row.unit;
      const line = document.createElement("div");
      line.className = "ct-lname";
      line.append(name, unit);
      el.appendChild(line);
      if (row.note !== undefined) {
        const note = document.createElement("div");
        note.className = "ct-lnote";
        note.textContent = row.note;
        el.appendChild(note);
      }
      labels.appendChild(el);
    }
  }

  function renderFoot(): void {
    const tab = activeTab();
    // Only the markers actually present are explained; a key listing letters
    // that appear nowhere teaches the reader to ignore the key.
    const present = new Set<string>();
    for (const row of tab?.rows ?? []) {
      for (const c of row.cells) if (c.mark !== undefined) present.add(c.mark);
    }
    keyEl.replaceChildren();
    for (const m of MARKS) {
      if (!present.has(m.mark)) continue;
      const s = document.createElement("span");
      const k = document.createElement("i");
      k.textContent = m.mark;
      s.append(k, document.createTextNode(m.meaning));
      keyEl.appendChild(s);
    }
    srcEl.textContent = tab?.source ?? "";
  }

  function renderScaleNote(): void {
    const tab = activeTab();
    const limited = (tab?.rows ?? []).some((r) => r.scale === "limit");
    if (input === null) {
      scaleText.textContent = "";
      return;
    }
    if (input.limit !== null && limited) {
      const v = input.vessel?.name.replace(/ —.*/, "") ?? "vessel";
      scaleText.textContent = `colour: against ${v} · ${input.opLabel ?? "operation"} limits`;
      scaleNote.title =
        `Each row is coloured against its OWN limit for this vessel and operation` +
        ` (waves ≤ ${input.limit.wave_m} m, wind ≤ ${input.limit.wind_kn} kn), so the row that` +
        ` turns red is the parameter stopping the job. The printed number is the` +
        ` measurement either way.`;
    } else {
      scaleText.textContent = "colour: absolute scale (no vessel + operation)";
      scaleNote.title =
        "No vessel and operation are selected, so there is no limit to colour against." +
        " The cells use a neutral ramp of each row's own range: more and less, not good and bad.";
    }
  }

  // --- Draw -----------------------------------------------------------------

  function layout(): void {
    const tab = activeTab();
    const rows = tab?.rows ?? [];
    let h = HEAD_H;
    for (const r of rows) h += r.height;
    view.style.height = `${h}px`;
    spacer.style.width = `${Math.max(1, columns.length * COL_W)}px`;
    requestDraw();
  }

  /** The per-cell direction glyph — chart/marks.ts's arrow, at this surface's
   *  own size and stroke weight. ONE shape for wind, wave and swell: the rows
   *  are labelled down the left edge, so the label names the quantity, exactly
   *  as the meteogram's panels do. This used to hand-roll a second dialect of
   *  the arrow (a 3.2-px head at a 35° half-angle against the ribbon's 3.4 and
   *  32°), which is how one glyph quietly becomes two. */
  function drawDir(
    cx: number,
    cy: number,
    dirDeg: number,
    color: string,
    toward: boolean,
  ): void {
    // The from/toward turn is chart/marks.ts's glyphHeading, not a second copy
    // of "+180" living here. 5.5 is the smallest this glyph is ever drawn — 11 px
    // in a 52-px column.
    g.save();
    g.strokeStyle = color;
    g.fillStyle = color;
    g.lineWidth = 1.2; // denser surface than the chart ribbon's 1.4
    g.lineCap = "round";
    g.lineJoin = "round";
    drawArrowGlyph(g, cx, cy, glyphHeading(dirDeg, toward), 5.5);
    g.restore();
  }

  function drawCell(
    row: Row,
    cell: Cell,
    x: number,
    y: number,
    palette: {
      surface: RGB;
      surface2: RGB;
      ink: RGB;
      ink2: RGB;
      muted: string;
      line: string;
      grades: RGB[];
      /** How far this theme's fills are pulled back to keep the digits legible. */
      scale: number;
    },
    past: boolean,
  ): void {
    const w = COL_W;
    const h = row.height;

    // Fill.
    let fill: RGB | null = null;
    if (row.kind === "daylight" && cell.night !== undefined) {
      const tint = NIGHT_TINTS[cell.night] ?? "";
      if (tint !== "") {
        g.fillStyle = tint;
        g.fillRect(x, y, w, h);
      }
    } else if (row.scale === "limit" && cell.p != null && Number.isFinite(cell.p)) {
      const gi = gradeIndexOf(cell.p);
      fill = gradeFill(gi, palette.grades[gi], palette.surface, palette.scale);
    } else if (cell.markGrade !== undefined) {
      // A derived condition with no grade of its own — a crossed sea, a
      // resonance, a squall hour. It tints, and the letter is printed with it.
      fill = gradeFill(cell.markGrade, palette.grades[cell.markGrade], palette.surface, palette.scale);
    } else if (row.scale === "absolute" && cell.value != null && Number.isFinite(cell.value)) {
      fill = neutralFill(cell.value, row.min ?? 0, row.max ?? 1, palette.ink2, palette.surface, palette.scale);
    }
    if (fill !== null) {
      g.fillStyle = toCss(fill);
      g.fillRect(x, y, w, h);
    }

    if (cell.text === "" && cell.mark === undefined) {
      // Absence prints an em dash in the muted ink and takes no colour. A blank
      // cell and a zero must never look alike.
      g.fillStyle = palette.muted;
      g.font = '10px var(--mono, ui-monospace), ui-monospace, monospace';
      g.textAlign = "center";
      g.textBaseline = "middle";
      g.fillText("—", x + w / 2, y + h / 2);
      return;
    }

    const bg = fill ?? palette.surface;
    const ink = readableInk(bg, [palette.ink, palette.surface]);
    if (cell.text !== "") {
      const ratio = contrastRatio(bg, ink);
      if (ratio < minContrast) minContrast = ratio;
    }
    const inkCss = toCss(ink);
    g.globalAlpha = past ? 0.45 : 1;

    const hasArrow = cell.dirDeg != null && Number.isFinite(cell.dirDeg);
    const hasSub = cell.sub !== undefined && cell.sub !== "";
    const textY = hasSub ? y + h / 2 - 4 : y + h / 2;

    if (hasArrow) drawDir(x + 11, textY, cell.dirDeg as number, inkCss, row.dirToward === true);

    g.fillStyle = inkCss;
    g.textAlign = hasArrow ? "left" : "center";
    g.textBaseline = "middle";
    g.font = '600 11.5px ui-monospace, SFMono-Regular, Menlo, monospace';
    g.fillText(cell.text, hasArrow ? x + 20 : x + w / 2, textY);

    if (hasSub) {
      g.font = '9.5px ui-monospace, SFMono-Regular, Menlo, monospace';
      g.globalAlpha = past ? 0.32 : 0.72;
      g.fillText(cell.sub as string, hasArrow ? x + 20 : x + w / 2, y + h / 2 + 7);
      g.globalAlpha = past ? 0.45 : 1;
    }

    if (cell.mark !== undefined) {
      g.font = '700 8.5px ui-monospace, SFMono-Regular, Menlo, monospace';
      g.textAlign = "right";
      g.textBaseline = "top";
      g.fillText(cell.mark, x + w - 3, y + 2);
    }

    g.globalAlpha = 1;
  }

  function draw(): void {
    const rect = view.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const wpx = Math.max(1, Math.round(rect.width * dpr));
    const hpx = Math.max(1, Math.round(rect.height * dpr));
    if (canvas.width !== wpx) canvas.width = wpx;
    if (canvas.height !== hpx) canvas.height = hpx;
    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);

    const surface = rgbVar("--surface");
    const ink = rgbVar("--ink");
    const ink2 = rgbVar("--ink-2");
    const grades = WORK_GRADES.map((gr): RGB => parseColor(cssVar(gr.varName, gr.fallback)) ?? GREY);
    const palette = {
      surface,
      surface2: rgbVar("--surface-2"),
      ink,
      ink2,
      muted: cssVar("--muted", FALLBACK["--muted"]),
      line: cssVar("--line", FALLBACK["--line"]),
      grades,
      // Measured once per draw, from the theme's own tokens: the strongest the
      // heatmap may be laid on and still keep every printed number at AA. The
      // neutral ink is measured alongside the grade colours so the absolute
      // scale cannot be the one that fails.
      scale: fitWeightScale(
        [
          ...grades.map((c, i): readonly [RGB, number] => [c, GRADE_WEIGHTS[i]]),
          [ink2, NEUTRAL_WEIGHT.max],
        ],
        surface,
        [ink, surface],
      ),
    };

    g.fillStyle = toCss(palette.surface);
    g.fillRect(0, 0, rect.width, rect.height);

    const tab = activeTab();
    if (input === null || tab === null || columns.length === 0) return;

    minContrast = Infinity;

    const off = scroll.scrollLeft;
    const firstCol = Math.max(0, Math.floor(off / COL_W));
    const lastCol = Math.min(columns.length - 1, Math.ceil((off + rect.width) / COL_W));
    const xOf = (c: number): number => c * COL_W - off;
    const nowIndex = input.fc.nowIndex;

    // Day header band.
    g.fillStyle = toCss(palette.surface2);
    g.fillRect(0, 0, rect.width, HEAD_H);
    for (let d = 0; d < days.length; d++) {
      const grp = days[d];
      if (grp.last < firstCol || grp.first > lastCol) continue;
      const x0 = xOf(grp.first);
      const x1 = xOf(grp.last + 1);
      g.save();
      g.beginPath();
      g.rect(x0, 0, x1 - x0, DAY_H);
      g.clip();
      g.fillStyle = cssVar("--ink", FALLBACK["--ink"]);
      g.textAlign = "left";
      g.textBaseline = "middle";
      g.font = '600 11px ui-sans-serif, system-ui, sans-serif';
      const label = fmtDayLong(instants[columns[grp.first]]);
      g.fillText(label, x0 + 7, DAY_H / 2);
      const badge = badges[d];
      if (badge !== null && badge !== undefined) {
        const word = badgeWords[d] ?? "";
        g.font = '600 9.5px ui-monospace, SFMono-Regular, Menlo, monospace';
        g.fillStyle = cssVar("--muted", FALLBACK["--muted"]);
        g.textAlign = "right";
        g.fillText(`${badge} ${word}`, x1 - 7, DAY_H / 2);
      }
      g.restore();
      g.strokeStyle = cssVar("--line-2", FALLBACK["--line-2"]);
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(Math.round(x0) + 0.5, 0);
      g.lineTo(Math.round(x0) + 0.5, rect.height);
      g.stroke();
    }
    g.strokeStyle = palette.line;
    g.beginPath();
    g.moveTo(0, DAY_H + 0.5);
    g.lineTo(rect.width, DAY_H + 0.5);
    g.moveTo(0, HEAD_H + 0.5);
    g.lineTo(rect.width, HEAD_H + 0.5);
    g.stroke();

    // Hour heads.
    g.font = '10px ui-monospace, SFMono-Regular, Menlo, monospace';
    g.textAlign = "center";
    g.textBaseline = "middle";
    for (let c = firstCol; c <= lastCol; c++) {
      const i = columns[c];
      g.fillStyle = i < nowIndex ? palette.muted : cssVar("--ink-2", FALLBACK["--ink-2"]);
      g.fillText(fmtHourCol(instants[i]), xOf(c) + COL_W / 2, DAY_H + HOUR_H / 2);
    }

    // Rows.
    let y = HEAD_H;
    for (const row of tab.rows) {
      for (let c = firstCol; c <= lastCol; c++) {
        drawCell(row, row.cells[c], xOf(c), y, palette, columns[c] < nowIndex);
      }
      y += row.height;
      g.strokeStyle = palette.line;
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(0, Math.round(y) + 0.5);
      g.lineTo(rect.width, Math.round(y) + 0.5);
      g.stroke();
    }

    // The playhead column — the same shared cursor the ruler and every track
    // follow, so the highlighted column here is the hour the chip above names.
    const activeCol = columnAtTime(columns, instants, getCursor());
    if (activeCol !== null && activeCol >= firstCol - 1 && activeCol <= lastCol + 1) {
      const x = xOf(activeCol);
      g.strokeStyle = cssVar("--accent", FALLBACK["--accent"]);
      g.lineWidth = 2;
      g.strokeRect(Math.round(x) + 1, 1, COL_W - 2, rect.height - 2);
    }

    if (minContrast === Infinity) minContrast = 21;
  }

  function requestDraw(): void {
    if (raf) return;
    raf = true;
    requestAnimationFrame(() => {
      raf = false;
      draw();
    });
  }

  // --- Interaction ----------------------------------------------------------

  function colAtClientX(clientX: number): number | null {
    const rect = view.getBoundingClientRect();
    const c = Math.floor((clientX - rect.left + scroll.scrollLeft) / COL_W);
    return c >= 0 && c < columns.length ? c : null;
  }

  function onClick(e: MouseEvent): void {
    const c = colAtClientX(e.clientX);
    if (c === null || input === null) return;
    // Clicking a column moves the shared playhead — the table is a timeline
    // surface like the ruler, not a separate view of one.
    setCursor(timeAtIndex(input.fc.times, columns[c]));
  }

  function onMove(e: MouseEvent): void {
    const c = colAtClientX(e.clientX);
    const tab = activeTab();
    if (c === null || tab === null || input === null) {
      scroll.title = "";
      return;
    }
    const rect = view.getBoundingClientRect();
    let y = e.clientY - rect.top - HEAD_H;
    if (y < 0) {
      scroll.title = "";
      return;
    }
    for (const row of tab.rows) {
      if (y < row.height) {
        const cell = row.cells[c];
        const bits = [`${row.label}${row.unit ? ` (${row.unit})` : ""}`];
        if (cell.text !== "") bits.push(cell.text + (cell.sub ? ` ${cell.sub}` : ""));
        if (cell.markTitle !== undefined) bits.push(cell.markTitle);
        if (row.scale === "limit" && cell.p != null) {
          bits.push(`${WORK_GRADES[gradeIndexOf(cell.p)].label} against this row's limit`);
        }
        scroll.title = bits.join(" · ");
        return;
      }
      y -= row.height;
    }
    scroll.title = "";
  }

  scroll.addEventListener("click", onClick);
  scroll.addEventListener("mousemove", onMove);
  scroll.addEventListener("scroll", requestDraw, { passive: true });

  function setCollapsed(next: boolean): void {
    root.classList.toggle("collapsed", next);
    setPref(PREF_OPEN, next ? "collapsed" : "open");
    if (!next) requestDraw();
  }
  if (getPref(PREF_OPEN) === "collapsed") root.classList.add("collapsed");
  title.addEventListener("click", () => {
    const next = !root.classList.contains("collapsed");
    setCollapsed(next);
    grip.setFolded(next); // the title is a second door to the same state
  });

  const unsubCursor = subscribeCursor(requestDraw);
  // A clock flip re-picks the columns (they are three-hourly in whichever clock
  // is being read) and regroups the days; a units flip re-renders every cell and
  // every row label. Both rebuild from the HELD forecast — neither refetches.
  const unsubClock = subscribeTimeMode(() => {
    if (input !== null) title.textContent = `Conditions · ${input.place} · 3-hourly, ${clockLabel()}`;
    rebuildColumns();
    rebuildTabs();
    renderTabs();
    renderLabels();
    renderFoot();
    layout();
  });
  const unsubUnits = subscribeUnits(() => {
    rebuildTabs();
    renderTabs();
    renderLabels();
    renderFoot();
    layout();
  });
  const ro = new ResizeObserver(requestDraw);
  ro.observe(view);

  return {
    update(next): void {
      input = next;
      root.hidden = next === null;
      if (next === null) return;
      title.textContent = `Conditions · ${next.place} · 3-hourly, ${clockLabel()}`;
      rebuildColumns();
      rebuildTabs();
      renderTabs();
      renderLabels();
      renderScaleNote();
      renderFoot();
      layout();
      // Open on the present rather than on two days of hindcast: a new point
      // should show the hours that can still be planned.
      const nowCol = columnAtTime(columns, instants, timeAtIndex(next.fc.times, next.fc.nowIndex));
      if (nowCol !== null) scroll.scrollLeft = Math.max(0, (nowCol - 1) * COL_W);
    },
    refreshSelection(patch): void {
      if (input === null) return;
      input = { ...input, ...patch };
      rebuildTabs();
      renderTabs();
      renderLabels();
      renderScaleNote();
      renderFoot();
      layout();
    },
    lastMinContrast(): number {
      return minContrast;
    },
    destroy(): void {
      unsubCursor();
      unsubClock();
      unsubUnits();
      ro.disconnect();
      scroll.removeEventListener("click", onClick);
      scroll.removeEventListener("mousemove", onMove);
      root.remove();
    },
  };
}
